import React, { useMemo } from 'react';
import Editor from '@monaco-editor/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CopyToClipboard } from 'components/common/ProjectIcons';
import {
  CODE_LANGUAGES,
  generateSnippet,
  languageLabel,
  monacoLanguageFor,
  SnippetKind,
} from './snippets';
import { usePreferredCodeLanguage } from './usePreferredCodeLanguage';
import { MONO_FONT_FAMILY } from 'fonts';

const TOAST_DURATION_MS = 2000;
const EDITOR_FONT_SIZE = 13;
const EDITOR_OPTIONS = {
  readOnly: true,
  fontFamily: MONO_FONT_FAMILY,
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  fontSize: EDITOR_FONT_SIZE,
  lineNumbers: 'off' as const,
  renderLineHighlight: 'none' as const,
  scrollbar: {
    vertical: 'hidden' as const,
    horizontal: 'hidden' as const,
    alwaysConsumeMouseWheel: false,
  },
  overviewRulerLanes: 0,
  folding: false,
  wordWrap: 'on' as const,
};

const pillBase =
  'px-3 py-1.5 text-sm rounded-full cursor-pointer transition-colors duration-200';
const pillActive = `${pillBase} bg-ui-cta-primary text-white`;
const pillInactive = `${pillBase} bg-unlisted-light-gray text-ui-text-secondary-text hover:text-ui-text-primary-text`;

type CodeSampleSwitcherProps = {
  endpoint: string;
  apiKey: string | null;
  integrationType: string;
  kind: SnippetKind;
  height: string;
};

const CodeSampleSwitcher = ({
  endpoint,
  apiKey,
  integrationType,
  kind,
  height,
}: CodeSampleSwitcherProps) => {
  const { t } = useTranslation();
  const [language, setLanguage] = usePreferredCodeLanguage();

  const snippet = useMemo(
    () =>
      generateSnippet(language, { endpoint, apiKey, integrationType, kind }),
    [language, endpoint, apiKey, integrationType, kind],
  );

  return (
    <div className="w-full">
      {apiKey !== null && (
        <p className="mb-2 max-w-[765px] rounded bg-unlisted-warning-yellow px-3 py-2 text-sm text-yellow-1">
          {t(
            'This code contains your real API key. Avoid sharing your screen while this is visible.',
          )}
        </p>
      )}
      <div
        role="group"
        aria-label={t('Code language')}
        className="mb-3 flex flex-row flex-wrap gap-2"
      >
        {CODE_LANGUAGES.map((lang) => {
          const isActive = lang === language;
          return (
            <button
              key={lang}
              type="button"
              aria-pressed={isActive}
              onClick={() => setLanguage(lang)}
              className={isActive ? pillActive : pillInactive}
            >
              {t(languageLabel(lang))}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => {
            navigator.clipboard
              .writeText(snippet)
              .then(() =>
                toast.info(t('Code sample copied to clipboard'), {
                  position: toast.POSITION.TOP_RIGHT,
                  autoClose: TOAST_DURATION_MS,
                }),
              )
              .catch(() =>
                toast.error(t('Failed to copy to clipboard'), {
                  position: toast.POSITION.TOP_RIGHT,
                  autoClose: TOAST_DURATION_MS,
                }),
              );
          }}
          className="ml-auto flex-shrink-0 text-ui-text-secondary-text hover:text-ui-text-primary-text"
          aria-label={t('Copy code sample')}
        >
          <CopyToClipboard />
        </button>
      </div>
      <div className="overflow-hidden rounded">
        <Editor
          height={height}
          language={monacoLanguageFor(language)}
          theme="vs"
          value={snippet}
          options={EDITOR_OPTIONS}
        />
      </div>
    </div>
  );
};

export default CodeSampleSwitcher;
